import React from "react";
import Konva from "konva";
import { Group, Path } from "react-konva";
import { IMondayTaskController } from "../../controller";
import Hoverable from "frontend/ui-components/konva-helpers/hoverable";
import useObservableController from "elements/hooks/use-observable-controller";
import tracking from "frontend/tracking";

const SIZE = 22;
const ICON_WIDTH = 12;
const ICON_HEIGHT = 7;

interface ExpandButtonProps extends Konva.NodeConfig {
  controller: IMondayTaskController;
}

export const ExpandButton = React.memo(({ controller, ...restProps }: ExpandButtonProps) => {
  useObservableController(controller, [
    "expanded-changed",
    "item-changed",
    "board-columns-changed",
    "hidden-columns-changed",
    "pinned-columns-changed",
  ]);
  const isExpanded = controller.element.expanded ?? false;
  const hasContent = controller.hasExpandableContent();

  function onExpand() {
    const newExpandedState = !isExpanded;
    tracking.trackAnalyticsEvent("monday_task_expanded", {
      expanded: newExpandedState,
    });
    controller.toggleExpanded();
  }

  return (
    <Group
      {...restProps}
      width={SIZE}
      height={SIZE}
      onClick={(e) => {
        e.evt.stopPropagation();
        e.evt.preventDefault();
        if (hasContent) {
          onExpand();
        }
      }}
      name="monday-task-expand-button"
    >
      <Hoverable
        cornerRadius={6}
        width={SIZE}
        height={SIZE}
        opacity={1}
        fill="transparent"
        hoverFill={hasContent ? "#F6F7FB" : "transparent"}
        cursor={hasContent ? "pointer" : "default"}
        listening={hasContent}
      />
      <Chevron disabled={!hasContent} expanded={isExpanded} />
    </Group>
  );
});

ExpandButton.displayName = "ExpandButton";

function Chevron({ disabled, expanded }: { disabled: boolean; expanded: boolean }) {
  const color = "#1E1E1E";
  if (disabled) return null;

  if (expanded) {
    return (
      <>
        <Path
          x={SIZE / 2 - ICON_WIDTH / 2}
          y={SIZE / 2 - ICON_HEIGHT / 2}
          stroke={color}
          strokeWidth={1.2}
          lineCap="round"
          data="M1 5.99995L5.99995 1"
          listening={false}
        />
        <Path
          x={SIZE / 2 - ICON_WIDTH / 2}
          y={SIZE / 2 - ICON_HEIGHT / 2}
          stroke={color}
          strokeWidth={1.2}
          lineCap="round"
          data="M10.9999 5.99995L6 1"
          listening={false}
        />
      </>
    );
  }

  return (
    <>
      <Path
        x={SIZE / 2 - ICON_WIDTH / 2}
        y={SIZE / 2 - ICON_HEIGHT / 2}
        stroke={color}
        strokeWidth={1.2}
        lineCap="round"
        data="M10.9999 1L6 5.99995"
        listening={false}
      />
      <Path
        x={SIZE / 2 - ICON_WIDTH / 2}
        y={SIZE / 2 - ICON_HEIGHT / 2}
        stroke={color}
        strokeWidth={1.2}
        lineCap="round"
        data="M1 1L5.99995 5.99995"
        listening={false}
      />
    </>
  );
}
